const STORAGE_KEY = 'fanuc.attribution';
const DEBUG_HASH = 'cache-debug';

const ATTRIBUTION_HANDLES = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'firstUtmSource',
  'firstUtmMedium',
  'firstUtmCampaign',
  'firstUtmTerm',
  'firstUtmContent',
  'first_utm_source',
  'first_utm_medium',
  'first_utm_campaign',
  'first_utm_term',
  'first_utm_content',
  'lastUtmSource',
  'lastUtmMedium',
  'lastUtmCampaign',
  'lastUtmTerm',
  'lastUtmContent',
  'last_utm_source',
  'last_utm_medium',
  'last_utm_campaign',
  'last_utm_term',
  'last_utm_content',
  'landingPageUrl',
  'landing_page_url',
  'currentPageUrl',
  'current_page_url',
  'referrerUrl',
  'referrer_url',
  'submissionUrl',
  'httpReferUrl',
];

function normalizeHandle(value) {
  return String(value || '')
    .replace(/^fields\[([^\]]+)]$/, '$1')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
}

function debugEnabled(windowObj) {
  return windowObj.location?.hash
    .split('#')
    .some((hash) => hash === DEBUG_HASH);
}

function parseStoredAttribution(localStorage) {
  try {
    return JSON.parse(localStorage?.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

export function redactToken(value) {
  if (!value) return '';

  return `${value.slice(0, 8)}...${value.slice(-6)} (${value.length})`;
}

export function summarizeCsrf(document) {
  const placeholders = document.querySelectorAll('craft-csrf-input');
  const inputs = document.querySelectorAll('input[name="CRAFT_CSRF_TOKEN"]');

  return {
    placeholders: placeholders.length,
    inputs: inputs.length,
    tokens: [...inputs].map((input) => redactToken(input.value)),
    hydrated: placeholders.length === 0 && inputs.length > 0,
  };
}

export function summarizeAttribution(document, localStorage) {
  const storage = parseStoredAttribution(localStorage);
  const handles = new Set(ATTRIBUTION_HANDLES.map(normalizeHandle));
  const inputs = [...document.querySelectorAll('input[name]')];
  const hiddenFields = inputs
    .filter((input) => input.type === 'hidden' || input.name.startsWith('fields['))
    .map((input) => input.name);
  const matchingFields = inputs
    .filter((input) => handles.has(normalizeHandle(input.name)))
    .map((input) => ({
      name: input.name,
      value: input.value,
    }));

  return {
    storageFound: Boolean(storage),
    storage,
    hiddenFields,
    matchingFields,
    populatedFields: matchingFields.filter((input) => input.value).length,
  };
}

export function shouldIgnoreMutations(mutations, panel) {
  return mutations.every((mutation) => panel?.contains(mutation.target));
}

function buildPanel(document) {
  const panel = document.createElement('aside');
  panel.id = 'cache-debug-panel';
  panel.style.cssText = [
    'position:fixed',
    'right:12px',
    'bottom:12px',
    'z-index:2147483647',
    'max-width:360px',
    'padding:12px',
    'border:1px solid #64748b',
    'background:#0f172a',
    'color:#f8fafc',
    'font:12px/1.4 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    'box-shadow:0 12px 40px rgba(15,23,42,.35)',
  ].join(';');
  document.body.appendChild(panel);

  return panel;
}

function renderPanel(panel, csrf, attribution) {
  const fieldStatus = attribution.matchingFields.length
    ? `${attribution.populatedFields}/${attribution.matchingFields.length} populated`
    : 'none found';

  panel.innerHTML = `
    <strong style="display:block;margin-bottom:6px;color:#facc15">Cache debug</strong>
    <div>CSRF: ${csrf.hydrated ? 'hydrated' : 'waiting'} (${csrf.inputs} inputs, ${csrf.placeholders} placeholders)</div>
    <div>Attribution storage: ${attribution.storageFound ? 'found' : 'missing'}</div>
    <div>Attribution fields: ${fieldStatus}</div>
    <div>Hidden fields: ${attribution.hiddenFields.length}</div>
    <div style="margin-top:6px;color:#cbd5e1">Use Network headers for actual Cloud cache HIT/MISS.</div>
  `;
}

export function initCacheDebug(windowObj = globalThis.window) {
  if (!windowObj || !debugEnabled(windowObj)) return null;

  const { document, localStorage } = windowObj;
  if (!document?.body) {
    document?.addEventListener('DOMContentLoaded', () => initCacheDebug(windowObj), {
      once: true,
    });
    return null;
  }

  const panel = buildPanel(document);

  const report = (label) => {
    const csrf = summarizeCsrf(document);
    const attribution = summarizeAttribution(document, localStorage);

    renderPanel(panel, csrf, attribution);
    console.log(`[cache-debug:${label}]`, { csrf, attribution });
  };

  report('initial');

  const observer = new windowObj.MutationObserver((mutations) => {
    if (shouldIgnoreMutations(mutations, panel)) return;

    report('mutation');
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  windowObj.addEventListener('load', () => report('load'));
  document.addEventListener('submit', () => report('submit'), true);

  return {
    report,
    observer,
    panel,
  };
}

if (typeof window !== 'undefined') {
  initCacheDebug(window);
}
